import { supabase } from "@/supabase/supabaseClient";
import { TokenManager } from "@/supabase/supabaseClient";
import type { Visit, VisitFormData, VisitWithDetails } from "@/types/visit";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export class VisitService {
  /**
   * Ensure we have a valid user context from the stored token
   */
  private static async getAuthenticatedUserId(): Promise<string> {
    const token = TokenManager.getStoredToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    // Re-establish Supabase session so auth headers are attached to queries
    await TokenManager.setSupabaseSession(token);

    const { data: userData, error: userError } =
      await supabase.auth.getUser(token);

    if (userError || !userData.user) {
      throw new Error("User not authenticated");
    }

    return userData.user.id;
  }

  /**
   * Get all visits for a specific task
   */
  static async getTaskVisits(
    taskId: string,
    tenantId: string
  ): Promise<VisitWithDetails[]> {
    const token = TokenManager.getStoredToken();
    if (!token) {
      console.error("[VisitService] No token available");
      return [];
    }

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/visits?task_id=eq.${taskId}&tenant_id=eq.${tenantId}&deleted_at=is.null&select=*,user_profiles!visits_user_id_fkey(id,email,first_name,last_name),visit_steps(id,step_id,is_primary,notes,steps(id,step_name,description,is_completed,sequence))&order=visited_at.desc`,
      {
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("[VisitService] Error response:", errorText);
      throw new Error(`Failed to fetch visits: ${response.status}`);
    }

    const data = await response.json();

    // Transform data to include user_name and steps_worked
    const visits: VisitWithDetails[] = (data || []).map((visit: any) => ({
      ...visit,
      user_name:
        visit.user_profiles?.first_name && visit.user_profiles?.last_name
          ? `${visit.user_profiles.first_name} ${visit.user_profiles.last_name}`
          : visit.user_profiles?.first_name ||
            visit.user_profiles?.last_name ||
            visit.user_profiles?.email ||
            "Unknown User",
      steps_worked: (visit.visit_steps || []).map((vs: any) => ({
        step_id: vs.step_id,
        step_title: vs.steps?.step_name || "Unknown Step",
        is_primary: vs.is_primary,
      })),
    }));

    return visits;
  }

  /**
   * Create a new visit
   */
  static async createVisit(
    visitData: VisitFormData,
    tenantId: string
  ): Promise<Visit> {
    const userId = await this.getAuthenticatedUserId();

    // Create the visit
    const { data: visit, error: visitError } = await supabase
      .from("visits")
      .insert({
        task_id: visitData.task_id,
        user_id: userId,
        visited_at: visitData.visited_at,
        notes: visitData.notes,
        duration_minutes: visitData.duration_minutes,
        created_by: userId,
        updated_by: userId,
        tenant_id: tenantId,
      })
      .select()
      .single();

    if (visitError || !visit) {
      console.error("Error creating visit:", visitError);
      throw new Error(visitError?.message || "Failed to create visit");
    }

    // If step_ids are provided, create visit_steps
    if (visitData.step_ids && visitData.step_ids.length > 0) {
      const visitSteps = visitData.step_ids.map((stepId) => ({
        visit_id: visit.id,
        step_id: stepId,
        is_primary: stepId === visitData.primary_step_id,
        tenant_id: tenantId,
      }));

      const { error: stepsError } = await supabase
        .from("visit_steps")
        .insert(visitSteps);

      if (stepsError) {
        console.error("Error creating visit steps:", stepsError);
        // Note: Visit is already created, so we don't roll back
      }
    }

    return visit;
  }

  /**
   * Update an existing visit
   */
  static async updateVisit(
    visitId: string,
    updates: Partial<VisitFormData>,
    tenantId: string
  ): Promise<{ success: boolean; error?: string }> {
    const userId = await this.getAuthenticatedUserId().catch((error) => {
      console.error("Error getting authenticated user:", error);
      return null;
    });

    if (!userId) {
      return { success: false, error: "User not authenticated" };
    }

    const { error } = await supabase
      .from("visits")
      .update({
        visited_at: updates.visited_at,
        notes: updates.notes,
        duration_minutes: updates.duration_minutes,
        updated_by: userId,
      })
      .eq("id", visitId)
      .eq("tenant_id", tenantId);

    if (error) {
      console.error("Error updating visit:", error);
      return { success: false, error: error.message };
    }

    // If step_ids are provided, update visit_steps
    if (updates.step_ids !== undefined) {
      // Delete existing visit_steps
      await supabase.from("visit_steps").delete().eq("visit_id", visitId);

      // Create new visit_steps
      if (updates.step_ids.length > 0) {
        const visitSteps = updates.step_ids.map((stepId) => ({
          visit_id: visitId,
          step_id: stepId,
          is_primary: stepId === updates.primary_step_id,
          tenant_id: tenantId,
        }));

        const { error: stepsError } = await supabase
          .from("visit_steps")
          .insert(visitSteps);

        if (stepsError) {
          console.error("Error updating visit steps:", stepsError);
        }
      }
    }

    return { success: true };
  }

  /**
   * Delete a visit (soft delete)
   */
  static async deleteVisit(
    visitId: string,
    tenantId: string
  ): Promise<{ success: boolean; error?: string }> {
    const userId = await this.getAuthenticatedUserId().catch((error) => {
      console.error("Error getting authenticated user:", error);
      return null;
    });

    if (!userId) {
      return { success: false, error: "User not authenticated" };
    }

    const { error } = await supabase
      .from("visits")
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
      })
      .eq("id", visitId)
      .eq("tenant_id", tenantId);

    if (error) {
      console.error("Error deleting visit:", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  }

  /**
   * Get visit statistics for a task
   */
  static async getTaskVisitStats(
    taskId: string,
    tenantId: string
  ): Promise<{
    total_visits: number;
    unique_visitors: number;
    total_duration_minutes: number;
    last_visit: string | null;
  }> {
    const token = TokenManager.getStoredToken();
    if (!token) {
      console.error("[VisitService] No token available for stats");
      return {
        total_visits: 0,
        unique_visitors: 0,
        total_duration_minutes: 0,
        last_visit: null,
      };
    }

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/visits?task_id=eq.${taskId}&tenant_id=eq.${tenantId}&deleted_at=is.null&select=user_id,visited_at,duration_minutes`,
      {
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("[VisitService] Stats error response:", errorText);
      return {
        total_visits: 0,
        unique_visitors: 0,
        total_duration_minutes: 0,
        last_visit: null,
      };
    }

    const data = await response.json();

    if (!data || data.length === 0) {
      return {
        total_visits: 0,
        unique_visitors: 0,
        total_duration_minutes: 0,
        last_visit: null,
      };
    }

    const uniqueVisitors = new Set(data.map((v: any) => v.user_id)).size;
    const totalDuration = data.reduce(
      (sum: number, v: any) => sum + (v.duration_minutes || 0),
      0
    );
    const lastVisit =
      data.length > 0
        ? data.sort(
            (a: any, b: any) =>
              new Date(b.visited_at).getTime() -
              new Date(a.visited_at).getTime()
          )[0].visited_at
        : null;

    return {
      total_visits: data.length,
      unique_visitors: uniqueVisitors,
      total_duration_minutes: totalDuration,
      last_visit: lastVisit,
    };
  }
}
